import { useState } from "react";
import { toast } from "sonner";
import { Check, CheckCircle, X, XCircle } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { useGet } from "@/hooks/useResource";
import { Loading, PageHeader, formatAuditTime, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const NOTES = [
  <>
    This service is provided <strong className="text-primary">complimentary</strong> — there is no
    charge.
  </>,
  <>Our content creator will work discreetly alongside your booked photographer / videographer.</>,
  <>
    All captured content remains the property of{" "}
    <strong className="text-primary">Gledswood Homestead &amp; Winery</strong> and may be used
    across our website, social media platforms (Instagram, Facebook, TikTok, Pinterest, etc.) and
    other marketing materials.
  </>,
  <>
    You can withdraw consent at any time before your wedding day by contacting our events team.
  </>,
];

function confirmCopy(decision, current) {
  const isChange = current && current !== decision;
  if (decision === "approved") {
    return isChange
      ? "Change your response to Approve? A Gledswood content creator will be welcome onsite on your wedding day."
      : "Confirm you're happy for a Gledswood content creator to be onsite on your wedding day to capture content for our social media and marketing use.";
  }
  return isChange
    ? "Change your response to Decline? Our content creator will not attend your wedding."
    : "Confirm you'd prefer no Gledswood content creator onsite on your wedding day.";
}

export default function Consent() {
  const { data, loading, refetch } = useGet("/client/consent");
  const [pending, setPending] = useState(null);
  const [saving, setSaving] = useState(null);

  const decision = data?.decision || "";
  const approved = decision === "approved";
  const declined = decision === "declined";

  const save = async () => {
    const next = pending;
    setPending(null);
    setSaving(next);
    try {
      await api.put("/client/consent", { decision: next });
      toast.success(next === "approved" ? "Consent approved." : "Consent declined.");
      refetch();
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return <Loading label="Loading consent" />;
  }

  return (
    <div data-testid="social-media-consent-page">
      <PageHeader
        eyebrow="Quick actions"
        title="Social Media Consent"
        description="One clear decision about Gledswood capturing content on your wedding day."
      >
        <StatusBadge
          status={decision ? "confirmed" : "pending"}
          at={data?.decided_at}
          by={data?.decided_by}
          confirmedLabel="Confirmed"
          testid="consent-status-badge"
        />
      </PageHeader>

      {decision && (
        <div
          className={`flex items-start gap-3 rounded-lg border p-5 ${
            approved ? "border-accent/40 bg-accent/10" : "border-border bg-muted/40"
          }`}
          data-testid="social-media-status-banner"
        >
          {approved ? (
            <CheckCircle size={22} weight="fill" className="mt-0.5 shrink-0 text-accent" />
          ) : (
            <XCircle size={22} className="mt-0.5 shrink-0 text-muted-foreground" />
          )}
          <div>
            <p className="font-heading text-xl text-primary" data-testid="social-media-status-title">
              {approved ? "Consent approved" : "Consent declined"}
            </p>
            <p className="mt-1 text-sm text-muted-foreground" data-testid="consent-confirmed-stamp">
              {formatAuditTime(data?.decided_at)}
              {data?.decided_by ? ` · ${data.decided_by}` : ""}
            </p>
          </div>
        </div>
      )}

      <article className="mt-6 max-w-3xl rounded-lg border border-border bg-card p-6 sm:p-8">
        <h2 className="font-heading text-2xl md:text-3xl text-primary">
          Social Media Content Consent
        </h2>
        <p className="mt-4 text-sm text-muted-foreground">
          We&apos;d love to capture the beauty of your wedding day for the Gledswood Homestead &amp;
          Winery social media channels and marketing materials.
        </p>
        <p className="mt-4 text-sm text-muted-foreground">
          By approving below, you consent to a Gledswood content creator being onsite on your wedding
          day to photograph and film moments across the venue — including your ceremony, reception
          and grounds.
        </p>
        <p className="eyebrow mt-8">Please note</p>
        <ul className="mt-4 space-y-3" data-testid="consent-notes">
          {NOTES.map((note, index) => (
            <li key={index} className="flex gap-3 text-sm text-muted-foreground">
              <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-secondary" />
              <span>{note}</span>
            </li>
          ))}
        </ul>
        <p className="mt-6 text-sm text-muted-foreground">
          If you&apos;d prefer not to have a Gledswood content creator onsite, simply decline below
          and we&apos;ll respect your wishes.
        </p>
      </article>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
        <Button
          className="rounded-full bg-primary hover:bg-primary/90 sm:min-w-44"
          disabled={!!saving}
          onClick={() => setPending("approved")}
          data-testid="consent-decision-approved"
        >
          <Check size={16} className="mr-1" />
          {saving === "approved" ? "Saving…" : approved ? "Approved" : "Approve"}
        </Button>
        <Button
          variant="outline"
          className={`rounded-full sm:min-w-44 ${declined ? "border-primary text-primary" : ""}`}
          disabled={!!saving}
          onClick={() => setPending("declined")}
          data-testid="consent-decision-declined"
        >
          <X size={16} className="mr-1" />
          {saving === "declined" ? "Saving…" : declined ? "Declined" : "Decline"}
        </Button>
      </div>
      {decision && (
        <p className="mt-4 text-xs text-muted-foreground" data-testid="consent-change-hint">
          Changed your mind? Choose the other option above to update your response.
        </p>
      )}

      <AlertDialog open={!!pending} onOpenChange={(open) => !open && setPending(null)}>
        <AlertDialogContent data-testid="consent-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-2xl text-primary">
              {pending === "approved" ? "Approve consent?" : "Decline consent?"}
            </AlertDialogTitle>
            <AlertDialogDescription>{confirmCopy(pending, decision)}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={`rounded-full ${
                pending === "declined"
                  ? "bg-destructive hover:bg-destructive/90"
                  : "bg-primary hover:bg-primary/90"
              }`}
              onClick={save}
              data-testid="consent-confirm-action"
            >
              {pending === "approved" ? "Approve" : "Decline"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
